import { Alert, AlertTitle, Button, IconButton, InputAdornment, Tooltip } from "@mui/material";
import { useMemo } from "react";
import {
    FieldError,
    FieldErrorsImpl,
    FieldValue,
    FieldValues,
    Merge,
    Path,
    useFieldArray,
    useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbPhone, TbTrash, TbUserHexagon } from "react-icons/tb";

import { getValueByPath } from "@/helpers/object";

import { CommonHelperText, CommonInput, CommonLabel } from "./CommonInput";

function CompanionsField<T extends FieldValues>({
    loading,
    formPath,
    handleResetLatency,
}: {
    loading: boolean;
    formPath: Path<T>;
    handleResetLatency: () => void;
}) {
    const { t } = useTranslation();
    const { control, register, formState } = useFormContext();
    const { fields, append, remove } = useFieldArray({ name: formPath as never, control });
    const error = useMemo(
        () =>
            getValueByPath<
                Merge<
                    FieldError,
                    (
                        | Merge<
                              FieldError,
                              FieldErrorsImpl<{
                                  name: string;
                                  phone: string;
                              }>
                          >
                        | undefined
                    )[]
                >
            >(formState.errors, formPath),
        [formState]
    );

    return (
        <CommonLabel labelClass="!px-8" label={t("companions")} className=" col-span-full">
            {fields.length ? (
                fields.map((field, index) => (
                    <div key={field.id} className=" grid grid-cols-[13px,8fr,8fr,1fr]  gap-3 pt-2">
                        <div className=" grid place-content-center  text-gray-500 translate-y-3 ">{index + 1}</div>
                        <CommonLabel className=" col-span-1" required label={t("name")}>
                            <CommonInput
                                disabled={loading}
                                error={!!error && !!error[index] && !!error[index]?.name?.message}
                                {...register(`${formPath}.${index}.name` as FieldValue<T>)}
                                placeholder={t("placeholders.enter", { field: t("name") })}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <TbUserHexagon />
                                    </InputAdornment>
                                }
                            />
                            <CommonHelperText text={(!!error && !!error[index] && error[index]?.name?.message) || ""} />
                        </CommonLabel>
                        <CommonLabel className=" col-span-1" label={t("phone")}>
                            <CommonInput
                                disabled={loading}
                                // error={!!error && !!error[index] && !!error[index]?.phone?.message}
                                {...register(`${formPath}.${index}.phone` as FieldValue<T>)}
                                placeholder={t("placeholders.enter", { field: t("phone") })}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <TbPhone />
                                    </InputAdornment>
                                }
                            />
                            <CommonHelperText
                                text={(!!error && !!error[index] && error[index]?.phone?.message) || ""}
                            />
                        </CommonLabel>
                        <div className="grid place-content-center font-semibold text-gray-500 translate-y-3">
                            <Tooltip title="Sil">
                                <IconButton
                                    disabled={loading}
                                    onClick={() => {
                                        handleResetLatency();
                                        remove(index);
                                    }}
                                    color="secondary"
                                >
                                    <TbTrash size={20} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                ))
            ) : (
                <Alert severity="info" className="col-span-full">
                    <AlertTitle>Refakatçi Eklenmedi</AlertTitle>
                    Refakatçi eklemek için <span className="font-semibold">"Yeni Refakatçi Ekle"</span> butonuna
                    tıklayabilirsiniz
                </Alert>
            )}
            <Button
                disabled={loading}
                onClick={() => {
                    handleResetLatency();
                    append({ name: "", phone: "" });
                }}
                className="!text-start w-1/2  !justify-start !pl-10 "
                classes={{ root: "!text-start" }}
            >
                Yeni Refakatçi Ekle
            </Button>
        </CommonLabel>
    );
}

export default CompanionsField;
